import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, MapPin, Instagram, Facebook, Twitter, Linkedin, Youtube } from 'lucide-react';
import { firmInfo } from '../data/mock';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  // Social media links - update these when accounts are created
  const socialLinks = [
    { 
      href: 'https://instagram.com/socialdisputeco', 
      icon: Instagram, 
      label: 'Instagram',
      active: true 
    },
    { 
      href: 'https://facebook.com/socialdisputeco', 
      icon: Facebook, 
      label: 'Facebook',
      active: true 
    },
    { 
      href: 'https://x.com/socialdisputeco', 
      icon: Twitter, 
      label: 'X (Twitter)',
      active: true 
    },
    { 
      href: 'https://linkedin.com/company/socialdisputeco', 
      icon: Linkedin, 
      label: 'LinkedIn',
      active: true 
    },
    { 
      href: 'https://youtube.com/@socialdisputeco', 
      icon: Youtube, 
      label: 'YouTube',
      active: true 
    },
  ];

  const quickLinks = [
    { href: '/about', label: 'About Us', isRoute: true },
    { href: '#services', label: 'Services', isRoute: false },
    { href: '#pricing', label: 'Pricing', isRoute: false },
    { href: '/faq', label: 'FAQ', isRoute: true },
    { href: '/contact', label: 'Contact', isRoute: true },
  ];

  const resourceLinks = [
    { href: '/analyze', label: 'Free Suspension Analyzer' },
    { href: '/letter', label: 'Free Appeal Letter Generator' },
    { href: '/tools/tos-tracker', label: 'ToS Change Tracker' },
    { href: '/data/reports', label: 'Enforcement Reports' },
    { href: '/tools/embed', label: 'Embed Widget on Your Site' },
    { href: '/press', label: 'Press Kit' },
    { href: '/notice-of-dispute', label: 'Notice of Dispute' },
    { href: '/arbitration-notice', label: 'Arbitration Guide' },
    { href: '/platform-registered-agent-addresses', label: 'Registered Agents' },
    { href: '/blog', label: 'Blog' },
  ];

  const legalLinks = [
    { href: '/privacy-policy', label: 'Privacy Policy' },
    { href: '/terms-of-service', label: 'Terms of Service' },
    { href: '/refund-policy', label: 'Refund Policy' },
    { href: '/advertising-disclosure', label: 'Advertising Disclosure' },
    { href: '/disclaimer', label: 'Disclaimer' },
    { href: '/trust', label: 'Verify Our Business' },
  ];

  const programLinks = [
    { href: '/referral-program', label: 'Referral Program - Earn $50' },
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer className="bg-[#1a1c1b] border-t border-[#3f4816]/50">
      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-[#d9fb06] flex items-center justify-center">
                <Shield className="w-7 h-7 text-[#1a1c1b]" />
              </div>
              <span className="text-2xl font-bold text-[#d9fb06] tracking-tight">
                {firmInfo.name}
              </span>
            </Link>
            <p className="text-[#888680] mb-6 text-sm">
              Formal dispute preparation and escalation for social media account suspensions.
            </p>
            <div className="flex flex-col gap-3">
              <a
                href={`mailto:${firmInfo.email}`}
                className="flex items-center gap-3 text-[#dfddd6] hover:text-[#d9fb06] transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-[#d9fb06]" />
                {firmInfo.email}
              </a>
              <a
                href={`https://instagram.com/${firmInfo.instagram.replace('@', '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-[#dfddd6] hover:text-[#d9fb06] transition-colors text-sm"
              >
                <Instagram className="w-4 h-4 text-[#d9fb06]" />
                {firmInfo.instagram}
              </a>
              <div className="flex items-center gap-3 text-[#888680] text-sm">
                <MapPin className="w-4 h-4 text-[#d9fb06]" />
                Honolulu, Hawaiʻi
              </div>
              
              {/* Social Media Links */}
              <div className="flex items-center gap-3 mt-4">
                {socialLinks.filter(s => s.active).map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-8 h-8 rounded-full bg-[#302f2c] flex items-center justify-center text-[#888680] hover:text-[#d9fb06] hover:bg-[#3f4816] transition-all"
                    aria-label={social.label}
                  >
                    <social.icon className="w-4 h-4" />
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[#d9fb06] font-bold uppercase tracking-wide mb-6 text-sm">
              Quick Links
            </h3>
            <nav className="flex flex-col gap-3">
              {quickLinks.map((link) => (
                link.isRoute ? (
                  <Link
                    key={link.href}
                    to={link.href}
                    className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                ) : (
                  <button
                    key={link.href}
                    onClick={() => scrollToSection(link.href)}
                    className="text-[#888680] hover:text-[#d9fb06] transition-colors text-left text-sm"
                  >
                    {link.label}
                  </button>
                )
              ))}
            </nav>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-[#d9fb06] font-bold uppercase tracking-wide mb-6 text-sm">
              Resources
            </h3>
            <nav className="flex flex-col gap-3">
              {resourceLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            
            {/* Legal Links */}
            <h3 className="text-[#d9fb06] font-bold uppercase tracking-wide mb-4 mt-6 text-sm">
              Legal
            </h3>
            <nav className="flex flex-col gap-3">
              {legalLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            
            {/* Referral Program */}
            <h3 className="text-[#d9fb06] font-bold uppercase tracking-wide mb-4 mt-6 text-sm">
              Programs
            </h3>
            <nav className="flex flex-col gap-3">
              {programLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="text-[#888680] hover:text-[#d9fb06] transition-colors text-sm font-medium"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Payment Info */}
          <div>
            <h3 className="text-[#d9fb06] font-bold uppercase tracking-wide mb-6 text-sm">
              Payment Options
            </h3>
            <div className="space-y-3 text-sm">
              <p>
                <span className="text-[#dfddd6] block">Venmo</span>
                <span className="text-[#888680]">@Socialdisputeco</span>
              </p>
              <p>
                <span className="text-[#dfddd6] block">Zelle</span>
                <span className="text-[#888680]">intake@socialdisputeco.com</span>
              </p>
              <p>
                <span className="text-[#dfddd6] block">ACH / Wire</span>
                <span className="text-[#888680]">Upon Request</span>
              </p>
            </div>
          </div>
        </div>

        {/* Compliance Disclosure Block */}
        <div className="mt-12 p-6 bg-[#1a1c1b] rounded-lg border border-[#3f4816]/30">
          <div className="space-y-4 text-xs text-[#888680]">
            <p>
              <span className="text-[#d9fb06] font-semibold">Independent Service Notice:</span> Social Dispute Co. is an independent documentation-preparation service. We are not affiliated with, endorsed by, or partnered with Meta, Instagram, Facebook, TikTok, YouTube, X, or any platform.
            </p>
            <p>
              <span className="text-[#d9fb06] font-semibold">No Guarantee:</span> We do not guarantee account reinstatement, review outcomes, or timelines. Platform decisions are outside our control and depend on platform review processes and case facts.
            </p>
            <p>
              <span className="text-[#d9fb06] font-semibold">Scope of Service:</span> We provide documentation preparation and organizational assistance only. We do not submit appeals on behalf of clients unless explicitly authorized through client-owned accounts or publicly available submission channels.
            </p>
            <p>
              <span className="text-[#d9fb06] font-semibold">Not Legal Advice:</span> Social Dispute Co. is not a law firm and does not provide legal advice. If legal representation is required, clients should consult a licensed attorney.
            </p>
            <p>
              <span className="text-red-400 font-semibold">Content Policy:</span> We do not accept cases involving Child Sexual Abuse Material (CSAM), child exploitation, or any content harmful to minors. Such inquiries will be immediately refused.
            </p>
            <p className="pt-2 border-t border-[#3f4816]/30">
              <span className="text-[#d9fb06] font-semibold">Business Identity:</span><br />
              Social Dispute Co.<br />
              Honolulu, Hawaii, USA<br />
              Email: intake@socialdisputeco.com
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-8 pt-8 border-t border-[#3f4816]/50 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-[#888680] text-sm">
            © {currentYear} {firmInfo.name}. All rights reserved.
          </p>
          <div className="flex gap-6 text-sm">
            <Link to="/disclaimer" className="text-[#888680] hover:text-[#d9fb06] transition-colors">
              Disclaimer
            </Link>
            <Link to="/faq" className="text-[#888680] hover:text-[#d9fb06] transition-colors">
              FAQ
            </Link>
            <Link to="/contact" className="text-[#888680] hover:text-[#d9fb06] transition-colors">
              Contact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
